'use client'

import Hls from 'hls.js'

import { useCallback, useRef } from 'react'
import { Media } from '@/payload-types'
import 'unmuted-lightgallery/dist/css/lightgallery.css'
import 'unmuted-lightgallery/dist/css/lg-zoom.css'
import 'unmuted-lightgallery/dist/css/lg-share.css'
import 'unmuted-lightgallery/dist/css/lg-thumbnail.css'
import 'unmuted-lightgallery/dist/css/lg-video.css'
import lgVideo from 'unmuted-lightgallery/dist/plugins/video'
import lgHash from 'unmuted-lightgallery/dist/plugins/hash'
import lgShare from 'unmuted-lightgallery/dist/plugins/share'
import lgThumbnail from 'unmuted-lightgallery/dist/plugins/thumbnail'
// @ts-expect-error this is a valid import
import LightGallery from 'unmuted-lightgallery/dist/react'
import { LightGallery as LightGalleryObject } from 'unmuted-lightgallery/dist/lightgallery'
import { GalleryItem } from 'unmuted-lightgallery/dist/lg-utils'
import CarouselGalleryComponent from '../carousel-gallery/carousel-gallery'

import CopyVideoLink from './lg-copy-link'
import { EmbeddedVideoItem, normalizedGallery } from './helperFunctionsLightGallery'

interface IProps {
  gallery?: (string | Media | EmbeddedVideoItem)[] | null
  externalVideos?: EmbeddedVideoItem[] | null
  children?: ({ onOpen }: { onOpen: (index: number) => void }) => React.ReactNode
  timeStamps?: {
    value: string 
    mediaId: string
  }[]
}

export const LightGalleryWrapper = ({ gallery = null, externalVideos, children, timeStamps }: IProps) => {
  const lightGallery = useRef<LightGalleryObject>(null)

  const onOpen = useCallback((index: number) => {
    lightGallery.current?.openGallery(index)
  }, [])

  const dynamicGallery: GalleryItem[] = normalizedGallery(gallery, timeStamps) ?? []
  if (externalVideos) {
    const normalizedExternalVideos = normalizedGallery(externalVideos, timeStamps)
    dynamicGallery.push(...normalizedExternalVideos)
  }
  const handleSlide = ({ index }: { index: number }) => {
    const instance = lightGallery.current

    const slideEl = instance?.getSlideItem?.(index)

    // Get the raw HTMLElement
    const slideNode = slideEl?.get?.() as HTMLElement

    const videoEl = slideNode?.querySelector?.('video')
    if (!videoEl) return

    const sourceEl = videoEl.querySelector('source')

    const src = sourceEl?.src
    if (!src?.endsWith('.m3u8')) return

    const startTimeAttr = videoEl?.getAttribute?.('data-start-time')
    const startTime = startTimeAttr ? Number(startTimeAttr) : 0

    if (Hls.isSupported()) {
      const hls = new Hls()
      hls.loadSource(src)
      hls.attachMedia(videoEl)
      videoEl.addEventListener(
        'loadedmetadata',
        () => {
          if (!isNaN(startTime)) {
            videoEl.currentTime = startTime
          }
        },
        { once: true },
      )
    } else {
      videoEl.src = src
    }
  }

  return (
    <LightGallery
      onInit={(detail: { instance: LightGalleryObject }) => {
        if (detail.instance) {
          lightGallery.current = detail.instance
        }
        const facebookLink = document.querySelector(
          '.lg-share-facebook',
        ) as HTMLAnchorElement | null
        if (facebookLink && !facebookLink.hasAttribute('href')) {
          facebookLink.href = ''
        }

        const twitterLink = document.querySelector('.lg-share-twitter') as HTMLAnchorElement | null
        if (twitterLink && !twitterLink.hasAttribute('href')) {
          twitterLink.href = ''
        }
      }}
      dynamic={true}
      plugins={[lgHash, lgShare, lgThumbnail, lgVideo, CopyVideoLink]}
      dynamicEl={dynamicGallery}
      pinterest={false}
      preload={0}
      mobileSettings={{ dynamic: true, zoom: true }}
      onAfterSlide={handleSlide}
    >
      {children ? (
        children({ onOpen })
      ) : (
        <CarouselGalleryComponent
          gallery={gallery as (string | Media)[] | null}
          externalVideos={externalVideos ?? null}
          onOpen={onOpen}
        />
      )}
    </LightGallery>
  )
}

export default LightGalleryWrapper
